/** The device notifications round (2026-11-01; brief docs/briefs/device-notifications-round.md rule 2, the red team's report findings 9, 10,
 *  12 and 18): knowing the device this page runs on, and the device panel's words for it. Pure: no React, no globals; the page reads the
 *  browser's facts once (`isStandalone(window)`, `navigator.maxTouchPoints`) and passes them in. Every sentence here is settings copy (at most
 *  30 words, sentence case), pinned in `device.test.ts` because the structure guard cannot see a lib file. */
export type DeviceKind = "mac" | "windows" | "linux" | "iphone" | "ipad" | "android" | "other";
export type DeviceFacts = { standalone?: boolean; touchPoints?: number };

/** which device this is: an iPad is an iPad string, or a Macintosh one with more than one touch point (iPadOS asks for the desktop site by
 *  default); Android before Linux (its string says Linux too); ChromeOS counts as a computer ("linux") */
export function deviceKind(userAgent: string, facts: DeviceFacts = {}): DeviceKind {
  const ua = userAgent ?? "";
  if (/\biPad\b/.test(ua)) return "ipad";
  if (/\b(iPhone|iPod)\b/.test(ua)) return "iphone";
  if (/\bAndroid\b/.test(ua)) return "android";
  if (/\bMacintosh\b/.test(ua)) return (facts.touchPoints ?? 0) > 1 ? "ipad" : "mac";
  if (/\bWindows\b/.test(ua)) return "windows";
  if (/\b(CrOS|Linux|X11)\b/.test(ua)) return "linux";
  return "other";
}

const WORDS: Record<DeviceKind, string> = { mac: "this Mac", windows: "this PC", linux: "this computer", iphone: "this iPhone", ipad: "this iPad", android: "this phone", other: "this computer" };
/** the device the way its owner calls it */
export const deviceWords = (kind: DeviceKind): string => WORDS[kind];

/** an iPhone or iPad: notifications only from the Home Screen */
export const isAppleTouch = (kind: DeviceKind): boolean => kind === "iphone" || kind === "ipad";

const BROWSERS: [RegExp, string][] = [[/\bEdg(e|A|iOS)?\//, "Edge"], [/\bOPR\//, "Opera"], [/\b(Firefox|FxiOS)\//, "Firefox"], [/\b(Headless)?(Chrome|CriOS|Chromium)\//, "Chrome"], [/\bVersion\/[\d.]+.*\bSafari\//, "Safari"]];
/** the browser that asks the permission question ("Chrome", "Safari", "Firefox", "Edge"); null when the string names none (an iPhone's Home
 *  Screen app) */
export const browserWord = (userAgent: string): string | null => BROWSERS.find(([re]) => re.test(userAgent ?? ""))?.[1] ?? null;

/** on a computer these browsers deliver only while they run (Safari on a Mac delivers with Safari closed) */
const RUNS_ALONE = new Set(["Chrome", "Edge", "Firefox", "Opera"]);
const COMPUTERS = new Set<DeviceKind>(["mac", "windows", "linux"]);
/** "even with OmnaBase closed", and on a computer whose browser must run, "as long as <browser> runs" */
function evenClosed(kind: DeviceKind, browser: string | null): string {
  return COMPUTERS.has(kind) && browser && RUNS_ALONE.has(browser) ? `even with OmnaBase closed, as long as ${browser} runs` : "even with OmnaBase closed";
}

/** the panel's one-sentence intro: where the notification shows on this device */
export function deviceIntro(kind: DeviceKind, browser: string | null): string {
  if (kind === "mac") return `A banner at the top right the moment something happens, ${evenClosed(kind, browser)}.`;
  if (kind === "windows") return `A notification at the bottom right the moment something happens, ${evenClosed(kind, browser)}.`;
  if (isAppleTouch(kind)) return `On the lock screen and in Notification Centre, like any app.`;
  if (kind === "android") return `In the status bar and the notification shade, even with ${browser ?? "the browser"} closed.`;
  return `A notification from this device within seconds, ${evenClosed(kind, browser)}.`;
}

/** the two Home Screen steps for an iPhone or iPad in a browser tab (iOS 26 may keep Share under the "···" button) */
export const HOME_SCREEN_STEPS = ["Tap Share (under ··· if you do not see it), then \"Add to Home Screen\".", "Open OmnaBase from that icon, sign in if asked, and turn notifications on there."] as const;

/** the line(s) under the state: the next step, fitted to the device */
export function deviceLines(kind: DeviceKind, state: string, o: { standalone: boolean; browser: string | null }): string[] {
  if (state === "homeScreen") return [...HOME_SCREEN_STEPS];
  if (state === "on") return ["On for this workspace only. Signing out here switches them off."];
  if (state === "unsupported") return ["This browser cannot get notifications from OmnaBase. The switches below still decide for your other devices."];
  if (state !== "off") return [];
  const ask = o.browser ? `when ${o.browser} asks` : "when asked";
  const lines = [`Turn on, then allow it ${ask}. The switches below decide what shows.`];
  if (kind === "android" && !o.standalone) lines.push("Add OmnaBase to the Home Screen to see its own icon on them.");
  return lines;
}

type WindowLike = { matchMedia?: (q: string) => { matches: boolean }; navigator?: { standalone?: boolean } };
/** whether the page was opened from a Home Screen icon (an installed web app); an old browser without `matchMedia` reads false */
export function isStandalone(win: unknown): boolean {
  const w = (win ?? {}) as WindowLike;
  try { if (w.matchMedia?.("(display-mode: standalone)").matches) return true; } catch { /* an old browser: not from the Home Screen */ }
  return w.navigator?.standalone === true;
}

/** the "Nothing shows?" fold: where this device's own settings may hold them back, then Focus (finding 19) */
export function nothingShows(kind: DeviceKind, browser: string | null): string[] {
  const app = browser ?? "your browser";
  const where: Record<DeviceKind, string> = {
    mac: `System Settings › Notifications › ${app}: allow notifications.`,
    windows: "Settings › System › Notifications: allow them for the browser.",
    iphone: "Settings › Notifications › OmnaBase: allow notifications.",
    ipad: "Settings › Notifications › OmnaBase: allow notifications.",
    android: `Settings › Apps › ${app} › Notifications: allow them.`,
    linux: "Check that your system allows notifications from this browser.",
    other: "Check that your system allows notifications from this browser.",
  };
  return [where[kind], "Focus or Do Not Disturb holds them until it ends."];
}

/** the drawing's look for a device and state: the Share sheet for an iPhone or iPad in a tab, the lock screen for one from the Home Screen */
export function stageKind(kind: DeviceKind, homeScreen: boolean): "mac" | "windows" | "iphone" | "android" | "homescreen" | "other" {
  if (isAppleTouch(kind)) return homeScreen ? "homescreen" : "iphone";
  if (kind === "mac" || kind === "windows" || kind === "android") return kind;
  return "other";
}

/** the drawing's sender line: a computer shows the browser and the site, a phone the app's name (finding 8) */
export const stageSender = (kind: DeviceKind, browser: string | null): string => (COMPUTERS.has(kind) ? `${browser ?? "Your browser"} · app.omnabase.com` : "OmnaBase");

/** the fixed sample, shaped like a real notification (the push sender titles one "Assigned to you by …" over the ticket's subject) */
export const STAGE_SAMPLE = { title: "Assigned to you by Mia", body: "Login fails after the update" } as const;
